import { useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { TypeSetupPrompt } from '@/components/onboarding/TypeSetupPrompt';
import { useBatchLedgers } from '@/api/hooks/useBatchLedgers';
import {
    useCreateLedger,
    useDeleteLedger,
    useConfirmLedgerUpdate,
    useRevertConfirmedDate,
    useReorderLedger,
} from '@/api/hooks/useLedgers';
import { useReorderCollection } from '@/api/hooks/useCollections';
import { useLedgerExport } from '@/hooks/useLedgerExport';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { LedgerFilters } from '@/components/ledgers/LedgerFilters';
import { LedgerList } from '@/components/ledgers/LedgerList';
import { LedgerForm } from '@/components/ledgers/LedgerForm';
import { DeleteLedgerDialog } from '@/components/ledgers/DeleteLedgerDialog';
import { LedgersSummaryCard } from '@/components/ledgers/LedgersSummaryCard';
import { getErrorMessage } from '@/utils/errorHandler';
import type { Ledger, LedgerType, CreateLedgerData, UpdateLedgerData, CollectionWithTotal } from '@/api/types';

interface LedgersListViewProps {
    type: LedgerType;
    /** Callback to set header action (download button) */
    onHeaderAction?: (node: ReactNode) => void;
}

/**
 * List view component for displaying ledgers.
 * Contains all list-specific logic: filtering, CRUD operations, and ledger list display.
 */
export function LedgersListView({ type, onHeaderAction }: LedgersListViewProps) {
    const { updateFromBatch, settings, hasAssetCollections, hasDebtCollections, refreshProfile } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const typeLabel = type === 'asset' ? 'Asset' : 'Debt';
    const typeLabelPlural = type === 'asset' ? 'Assets' : 'Debts';

    // Parse URL params for initial state
    const parseNumberArray = (param: string | null): number[] => {
        if (!param) return [];
        return param.split(',').map(Number).filter(n => !isNaN(n));
    };

    // Filter state - synced with URL params
    const [selectedCollectionIds, setSelectedCollectionIds] = useState<number[]>(() =>
        parseNumberArray(searchParams.get('collections'))
    );
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>(() =>
        parseNumberArray(searchParams.get('tags'))
    );
    // Pending tags - updated on each selection, applied on dropdown close
    const [pendingTagIds, setPendingTagIds] = useState<number[]>(() =>
        parseNumberArray(searchParams.get('tags'))
    );
    const [showArchived, setShowArchived] = useState(() =>
        searchParams.get('archived') === 'true'
    );

    // Per-ledger loading state for confirmation actions
    const [updatingLedgerId, setUpdatingLedgerId] = useState<number | null>(null);

    // Track when related queries are still refetching (to keep spinner visible)
    const confirmRelatedFetchingCount = useIsFetching({
        predicate: (query) => {
            const key = query.queryKey[0];
            return ['ledgers', 'batch-ledgers', 'ledger-detail-batch',
                    'ledger-stats', 'batch-dashboard-analytics'].includes(key as string);
        },
    });
    const isConfirmRefetching = confirmRelatedFetchingCount > 0;

    // Sync state changes to URL
    const updateUrlParams = useCallback((
        collections: number[],
        tags: number[],
        archived: boolean
    ) => {
        const params = new URLSearchParams(searchParams);
        // Preserve view param if present
        const view = params.get('view');

        params.delete('collections');
        params.delete('tags');
        params.delete('archived');

        if (collections.length > 0) {
            params.set('collections', collections.join(','));
        }
        if (tags.length > 0) {
            params.set('tags', tags.join(','));
        }
        if (archived) {
            params.set('archived', 'true');
        }
        if (view) {
            params.set('view', view);
        }
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    // Wrapped setters that also update URL
    const handleCollectionChange = useCallback((ids: number[]) => {
        setSelectedCollectionIds(ids);
        updateUrlParams(ids, selectedTagIds, showArchived);
    }, [selectedTagIds, showArchived, updateUrlParams]);

    // Update pending tags on each selection (UI only, no filter update)
    const handleTagsChange = useCallback((ids: number[]) => {
        setPendingTagIds(ids);
    }, []);

    // Apply pending tags on dropdown close (triggers filter update)
    const handleTagsClose = useCallback(() => {
        setSelectedTagIds(pendingTagIds);
        updateUrlParams(selectedCollectionIds, pendingTagIds, showArchived);
    }, [pendingTagIds, selectedCollectionIds, showArchived, updateUrlParams]);

    const handleShowArchivedChange = useCallback((archived: boolean) => {
        setShowArchived(archived);
        updateUrlParams(selectedCollectionIds, selectedTagIds, archived);
    }, [selectedCollectionIds, selectedTagIds, updateUrlParams]);

    // Reset all filters
    const handleResetFilters = useCallback(() => {
        setSelectedCollectionIds([]);
        setSelectedTagIds([]);
        setPendingTagIds([]);
        setShowArchived(false);
        const params = new URLSearchParams();
        const view = searchParams.get('view');
        if (view) {
            params.set('view', view);
        }
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    // Dialog state
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [deletingLedger, setDeletingLedger] = useState<Ledger | null>(null);
    const [showCollectionWarning, setShowCollectionWarning] = useState(false);

    // Fetch data (always includes archived for correct P&L calculation)
    const {
        data: batchData,
        isLoading,
        error,
    } = useBatchLedgers({ type });

    // Mutations
    const createLedger = useCreateLedger();
    const deleteLedger = useDeleteLedger();
    const confirmUpdate = useConfirmLedgerUpdate();
    const revertConfirmedDate = useRevertConfirmedDate();
    const reorderLedger = useReorderLedger();
    const reorderCollection = useReorderCollection();

    // Update AuthContext with batch data when it loads
    useEffect(() => {
        if (batchData?.profile?.user && batchData?.profile?.settings) {
            updateFromBatch(batchData.profile.user, batchData.profile.settings);
        }
    }, [batchData, updateFromBatch]);

    // Clear updating state when mutation AND refetch are both done
    useEffect(() => {
        if (updatingLedgerId !== null &&
            !isConfirmRefetching &&
            !confirmUpdate.isPending &&
            !revertConfirmedDate.isPending) {
            setUpdatingLedgerId(null);
        }
    }, [updatingLedgerId, isConfirmRefetching, confirmUpdate.isPending, revertConfirmedDate.isPending]);

    // Get ledgers, collections, and tags - memoized to prevent unnecessary re-renders
    const allLedgers = useMemo(
        () => batchData?.ledgers?.items || [],
        [batchData?.ledgers?.items]
    );
    const collections = useMemo(
        () => batchData?.collections?.items || [],
        [batchData?.collections?.items]
    );
    const tags = useMemo(
        () => batchData?.tags?.items || [],
        [batchData?.tags?.items]
    );

    // Get hasArchivedLedgers from profile response
    const hasArchivedLedgers = useMemo(() => {
        if (type === 'asset') {
            return batchData?.profile?.hasArchivedAssets ?? false;
        }
        return batchData?.profile?.hasArchivedDebts ?? false;
    }, [batchData?.profile, type]);

    // Get userLatestConfirmationDate for up-to-date status calculation
    const userLatestConfirmationDate = batchData?.ledgers?.userLatestConfirmationDate ?? null;

    // Calculate total from ALL non-archived ledgers (for percentage calculation)
    // Archived ledgers should not contribute to total value
    const allLedgersTotal = useMemo(() => {
        return allLedgers
            .filter(l => !l.archivedAt)
            .reduce((sum, l) => sum + (l.latestValue?.convertedValue ?? 0), 0);
    }, [allLedgers]);

    // Compute collection totals
    const collectionsWithTotals: CollectionWithTotal[] = useMemo(() => {
        return collections.map((collection) => {
            const collectionLedgers = allLedgers.filter(
                (l) => l.collectionId === collection.id
            );
            const total = collectionLedgers.reduce(
                (sum, l) => sum + (l.latestValue?.convertedValue ?? 0),
                0
            );
            return {
                ...collection,
                total,
                ledgerCount: collectionLedgers.length,
            };
        });
    }, [collections, allLedgers]);

    // Apply client-side filters
    const filteredLedgers = useMemo(() => {
        let result = allLedgers;

        // Filter archived ledgers for display (hide unless showArchived is true)
        if (!showArchived) {
            result = result.filter((l) => !l.archivedAt);
        }

        // Filter by collections (multi-select, empty = all)
        if (selectedCollectionIds.length > 0) {
            result = result.filter((l) => selectedCollectionIds.includes(l.collectionId));
        }

        // Filter by tags (show ledgers having ANY selected tag)
        if (selectedTagIds.length > 0) {
            result = result.filter((l) =>
                l.tagIds.some((tagId) => selectedTagIds.includes(tagId))
            );
        }

        return result;
    }, [allLedgers, selectedCollectionIds, selectedTagIds, showArchived]);

    // Calculate filtered totals from ledgers (always calculated on frontend now)
    const hasClientFilters = selectedCollectionIds.length > 0 || selectedTagIds.length > 0;

    const { filteredTotal, filteredProfitLoss } = useMemo(() => {
        let total = 0;
        let profitLoss = 0;
        let hasAnyPnl = false;

        // Get ledgers matching filters (including archived) for P&L calculation
        let matchingLedgers = allLedgers;

        if (selectedCollectionIds.length > 0) {
            matchingLedgers = matchingLedgers.filter(
                l => selectedCollectionIds.includes(l.collectionId)
            );
        }

        if (selectedTagIds.length > 0) {
            matchingLedgers = matchingLedgers.filter(
                l => l.tagIds.some(tagId => selectedTagIds.includes(tagId))
            );
        }

        // Calculate P&L from matching ledgers (including archived)
        for (const ledger of matchingLedgers) {
            if (ledger.analytics?.profitLoss != null) {
                profitLoss += ledger.analytics.profitLoss;
                hasAnyPnl = true;
            }
        }

        // Calculate total value from filtered ledgers (excluding archived)
        for (const ledger of filteredLedgers) {
            if (!ledger.archivedAt) {
                total += ledger.latestValue?.convertedValue ?? 0;
            }
        }

        return {
            filteredTotal: total,
            filteredProfitLoss: hasAnyPnl ? profitLoss : null,
        };
    }, [allLedgers, filteredLedgers, selectedCollectionIds, selectedTagIds]);

    // Calculate percentage of total when filters are active
    const percentageOfTotal = useMemo(() => {
        if (!hasClientFilters || allLedgersTotal === 0) {
            return null; // No percentage when showing everything or total is 0
        }
        return (filteredTotal / allLedgersTotal) * 100;
    }, [hasClientFilters, filteredTotal, allLedgersTotal]);

    // Determine if filters are applied (for reset button visibility)
    const hasActiveFilters = selectedCollectionIds.length > 0 || selectedTagIds.length > 0 || showArchived;

    // Drag is only enabled when exactly one collection is selected and not showing archived
    const isDragEnabled = selectedCollectionIds.length === 1 && !showArchived;

    // Handle add button click - validate single collection selection
    const handleAddClick = useCallback(() => {
        if (selectedCollectionIds.length !== 1) {
            setShowCollectionWarning(true);
            return;
        }
        setIsCreateDialogOpen(true);
    }, [selectedCollectionIds.length]);

    // Get the selected collection ID for create form (when exactly 1 selected)
    const selectedCollectionId = selectedCollectionIds.length === 1 ? selectedCollectionIds[0] : undefined;

    // Set up header export button
    useLedgerExport({ ledgers: filteredLedgers, type, onHeaderAction });

    // Handlers
    const handleCreate = async (data: CreateLedgerData | UpdateLedgerData) => {
        await createLedger.mutateAsync(data as CreateLedgerData);
        setIsCreateDialogOpen(false);
    };

    const handleDelete = async (id: number) => {
        await deleteLedger.mutateAsync(id);
        // Refresh profile to sync onboarding state (hasAssetCollections/hasDebtCollections)
        await refreshProfile();
        setDeletingLedger(null);
    };

    const handleConfirmUpdate = async (ledgerId: number) => {
        setUpdatingLedgerId(ledgerId);
        try {
            await confirmUpdate.mutateAsync(ledgerId);
            // Effect clears when refetch completes
        } catch {
            setUpdatingLedgerId(null); // Clear immediately on error
        }
    };

    const handleRevertUpdate = async (ledgerId: number) => {
        setUpdatingLedgerId(ledgerId);
        try {
            await revertConfirmedDate.mutateAsync(ledgerId);
            // Effect clears when refetch completes
        } catch {
            setUpdatingLedgerId(null); // Clear immediately on error
        }
    };

    const handleReorder = async (ledgerId: number, newWeight: number) => {
        await reorderLedger.mutateAsync({
            id: ledgerId,
            weight: newWeight,
            includeArchived: showArchived,
        });
    };

    const handleCollectionReorder = async (collectionId: number, newWeight: number) => {
        await reorderCollection.mutateAsync({ id: collectionId, weight: newWeight });
    };

    const isUpdating =
        createLedger.isPending ||
        deleteLedger.isPending ||
        confirmUpdate.isPending ||
        revertConfirmedDate.isPending ||
        reorderLedger.isPending ||
        reorderCollection.isPending;

    // Error state
    if (error) {
        return <ErrorAlert message={getErrorMessage(error)} />;
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <div className="text-gray-500 dark:text-gray-400">
                    Loading {typeLabelPlural.toLowerCase()}...
                </div>
            </div>
        );
    }

    // No Collections Empty State - show TypeSetupPrompt if user needs onboarding for this type
    if (collections.length === 0) {
        const needsTypeSetup = type === 'asset' ? !hasAssetCollections : !hasDebtCollections;

        if (needsTypeSetup) {
            return (
                <TypeSetupPrompt
                    type={type}
                    onComplete={() => {
                        // Force a re-render by navigating to the same page
                        navigate(type === 'asset' ? '/assets' : '/debts', { replace: true });
                    }}
                />
            );
        }

        // User has some collections but none for this type (shouldn't normally happen after onboarding)
        return (
            <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary p-8 text-center">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                    No {type === 'asset' ? 'asset' : 'debt'} collections yet.
                </p>
                <p className="text-gray-500 dark:text-gray-500 mb-6">
                    Create a collection first to start tracking your {typeLabelPlural.toLowerCase()}.
                </p>
                <Link
                    to="/settings/attribution"
                    className="inline-flex items-center px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
                >
                    Manage Collections
                </Link>
            </div>
        );
    }

    return (
        <>
            {/* Filters */}
            <LedgerFilters
                collections={collectionsWithTotals}
                tags={tags}
                selectedCollectionIds={selectedCollectionIds}
                selectedTagIds={pendingTagIds}
                showArchived={showArchived}
                onCollectionChange={handleCollectionChange}
                onCollectionReorder={handleCollectionReorder}
                onTagsChange={handleTagsChange}
                onTagsClose={handleTagsClose}
                onShowArchivedChange={handleShowArchivedChange}
                hasArchivedLedgers={hasArchivedLedgers}
                hasActiveFilters={hasActiveFilters}
                onReset={handleResetFilters}
                type={type}
            />

            <LedgersSummaryCard
                type={type}
                filteredTotal={filteredTotal}
                percentageOfTotal={percentageOfTotal}
                filteredProfitLoss={filteredProfitLoss}
                action={
                    <button
                        type="button"
                        onClick={handleAddClick}
                        title={`Add ${typeLabel}`}
                        className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center text-white bg-accent rounded-full hover:bg-accent/90 transition-colors shrink-0 shadow-md"
                    >
                        <PlusIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                    </button>
                }
            />

            {/* Empty State */}
            {filteredLedgers.length === 0 && (
                <div className="rounded-lg border border-gray-300 bg-white py-12 text-center dark:border-gray-700 dark:bg-secondary">
                    {allLedgers.length === 0 ? (
                        <>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                Add Your First {typeLabel}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Select a collection from the filter above, then click the + button.
                            </p>
                        </>
                    ) : (
                        <p className="text-gray-500 dark:text-gray-400">
                            No {typeLabelPlural.toLowerCase()} match your filters.
                        </p>
                    )}
                </div>
            )}

            {/* Ledger List */}
            {filteredLedgers.length > 0 && (
                <LedgerList
                    ledgers={filteredLedgers}
                    type={type}
                    onConfirmUpdate={handleConfirmUpdate}
                    onRevertUpdate={handleRevertUpdate}
                    onReorder={handleReorder}
                    isUpdating={isUpdating}
                    isDragEnabled={isDragEnabled}
                    userLatestConfirmationDate={userLatestConfirmationDate}
                    updatingLedgerId={updatingLedgerId}
                />
            )}

            {/* Collection Selection Warning Dialog */}
            <Dialog
                open={showCollectionWarning}
                dismissible={false}
                onClose={() => setShowCollectionWarning(false)}
                title="Select a Collection"
            >
                <div className="space-y-4">
                    <p className="text-gray-600 dark:text-gray-400">
                        Please select exactly one collection to add a new {typeLabel.toLowerCase()}.
                    </p>
                    <p className="text-gray-500 dark:text-gray-500 text-sm">
                        The new {typeLabel.toLowerCase()} will be added to the selected collection.
                    </p>
                    <div className="flex justify-end">
                        <Button
                            variant="primary"
                            onClick={() => setShowCollectionWarning(false)}
                        >
                            OK
                        </Button>
                    </div>
                </div>
            </Dialog>

            {/* Create Dialog */}
            <Dialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                title={`Add ${typeLabel}`}
                size="2xl"
            >
                <LedgerForm
                    type={type}
                    collections={collections}
                    tags={tags}
                    onSubmit={handleCreate}
                    onCancel={() => setIsCreateDialogOpen(false)}
                    isLoading={createLedger.isPending}
                    defaultCurrency={settings?.displayCurrency || 'USD'}
                    preselectedCollectionId={selectedCollectionId}
                />
            </Dialog>

            {/* Delete Dialog */}
            <DeleteLedgerDialog
                ledger={deletingLedger}
                type={type}
                open={!!deletingLedger}
                onClose={() => setDeletingLedger(null)}
                onConfirm={handleDelete}
                isLoading={deleteLedger.isPending}
            />

        </>
    );
}
